import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Plus, Users, Mail, Phone, Briefcase, Edit, X, Clock, QrCode, Send } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';

const EmployeesList = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [loadingQr, setLoadingQr] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [qrEmployeeId, setQrEmployeeId] = useState(null);
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm();

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await api.get('/employees');
      setEmployees(response.data);
    } catch (error) {
      toast.error('Błąd podczas pobierania pracowników');
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (employee) => {
    setSelectedEmployee(employee);
    setValue('firstName', employee.firstName || '');
    setValue('lastName', employee.lastName || '');
    setValue('email', employee.email || '');
    setValue('phoneNumber', employee.phoneNumber || '');
    setValue('position', employee.position || '');
    setValue('active', employee.active !== undefined ? String(employee.active) : 'true');
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setSelectedEmployee(null);
    reset();
  };

  const onEditSubmit = async (data) => {
    setSaving(true);
    try {
      const updateData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phoneNumber: data.phoneNumber || null,
        position: data.position || null,
        active: data.active === 'true' || data.active === true,
      };

      await api.put(`/employees/${selectedEmployee.id}`, updateData);
      toast.success('Pracownik został zaktualizowany');
      handleCloseModal();
      fetchEmployees();
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Błąd podczas aktualizacji pracownika';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (employee) => {
    try {
      await api.patch(`/employees/${employee.id}/active`, { active: !employee.active });
      toast.success(`Pracownik został ${employee.active ? 'dezaktywowany' : 'aktywowany'}`);
      fetchEmployees();
    } catch (error) {
      toast.error('Błąd podczas zmiany statusu pracownika');
    }
  };

  const handleDelete = async (employee) => {
    if (!window.confirm(`Czy na pewno chcesz usunąć pracownika ${employee.firstName} ${employee.lastName}?`)) {
      return;
    }
    try {
      await api.delete(`/employees/${employee.id}`);
      toast.success('Pracownik został usunięty');
      fetchEmployees();
    } catch (error) {
      toast.error('Błąd podczas usuwania pracownika');
    }
  };

  const handleViewTimeEntries = (employeeId) => {
    navigate(`/app/hr/time-entries/${employeeId}`);
  };

  const handleShowQrCode = async (employee) => {
    setLoadingQr(true);
    setShowQrModal(true);
    setQrEmployeeId(employee.id);
    try {
      const response = await api.get(`/employees/${employee.id}/qr-code`);
      setQrCodeData(response.data);
    } catch (error) {
      toast.error('Błąd podczas pobierania kodu QR');
      console.error('Error fetching QR code:', error);
      setShowQrModal(false);
      setQrEmployeeId(null);
    } finally {
      setLoadingQr(false);
    }
  };

  const handleCopyQrCode = () => {
    if (qrCodeData?.qrCodeValue) {
      navigator.clipboard.writeText(qrCodeData.qrCodeValue);
      toast.success('Kod QR został skopiowany do schowka');
    }
  };

  const handleSendQrCodeByEmail = async () => {
    if (!qrCodeData?.employeeEmail) {
      toast.error('Pracownik nie ma przypisanego adresu email');
      return;
    }

    if (!qrEmployeeId) {
      toast.error('Błąd: brak ID pracownika');
      return;
    }

    setSendingEmail(true);
    try {
      await api.post(`/employees/${qrEmployeeId}/qr-code/send-email`);
      toast.success(`Kod QR został wysłany na adres: ${qrCodeData.employeeEmail}`);
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Błąd podczas wysyłania kodu QR na email';
      toast.error(errorMessage);
      console.error('Error sending QR code by email:', error);
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Lista pracowników</h1>
        <button
          onClick={() => navigate('/app/hr/employees/add')}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Dodaj pracownika
        </button>
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pracownik
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Telefon
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stanowisko
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Akcje
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {employees.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    Brak pracowników. Dodaj pierwszego pracownika.
                  </td>
                </tr>
              ) : (
                employees.map((employee) => (
                  <tr key={employee.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <Users className="w-5 h-5 text-primary-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {employee.firstName} {employee.lastName}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-500">
                        <Mail className="w-4 h-4 mr-2" />
                        {employee.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {employee.phoneNumber ? (
                        <div className="flex items-center">
                          <Phone className="w-4 h-4 mr-2" />
                          {employee.phoneNumber}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {employee.position ? (
                        <div className="flex items-center">
                          <Briefcase className="w-4 h-4 mr-2" />
                          {employee.position}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          employee.active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {employee.active ? 'Aktywny' : 'Nieaktywny'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2 items-center">
                        <button
                          onClick={() => handleShowQrCode(employee)}
                          className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-md transition-colors flex items-center gap-1.5 shadow-md font-medium text-xs"
                          title="Kod QR"
                        >
                          <QrCode className="w-4 h-4" strokeWidth={2.5} />
                          <span>QR</span>
                        </button>
                        <button
                          onClick={() => handleViewTimeEntries(employee.id)}
                          className="text-primary-600 hover:text-primary-900 p-1.5 rounded hover:bg-primary-50 transition-colors flex items-center justify-center"
                          title="Godziny pracy"
                        >
                          <Clock className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditClick(employee)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edytuj"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleActive(employee)}
                          className={`${
                            employee.active
                              ? 'text-yellow-600 hover:text-yellow-900'
                              : 'text-green-600 hover:text-green-900'
                          }`}
                          title={employee.active ? 'Dezaktywuj' : 'Aktywuj'}
                        >
                          {employee.active ? '⏸' : '▶'}
                        </button>
                        <button
                          onClick={() => handleDelete(employee)}
                          className="text-red-600 hover:text-red-900"
                          title="Usuń"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal edycji */}
      {showEditModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Edytuj pracownika</h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleSubmit(onEditSubmit)} className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Imię *</label>
                  <input
                    {...register('firstName', { required: 'Imię jest wymagane' })}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Nazwisko *</label>
                  <input
                    {...register('lastName', { required: 'Nazwisko jest wymagane' })}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Email *</label>
                  <input
                    {...register('email', {
                      required: 'Email jest wymagany',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Nieprawidłowy email'
                      }
                    })}
                    type="email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Telefon</label>
                  <input
                    {...register('phoneNumber')}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Stanowisko</label>
                  <input
                    {...register('position')}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Status</label>
                  <select
                    {...register('active')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="true">Aktywny</option>
                    <option value="false">Nieaktywny</option>
                  </select>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Anuluj
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                  >
                    {saving ? 'Zapisywanie...' : 'Zapisz'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal kodu QR */}
      {showQrModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Kod QR pracownika</h3>
                <button
                  onClick={() => {
                    setShowQrModal(false);
                    setQrCodeData(null);
                    setQrEmployeeId(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {loadingQr ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="lg" />
                </div>
              ) : qrCodeData ? (
                <div className="space-y-4">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">
                      {qrCodeData.employeeName}
                    </p>
                    <div className="flex justify-center p-4 bg-gray-50 rounded-lg">
                      <img
                        src={qrCodeData.qrCode}
                        alt="QR Code"
                        className="w-64 h-64"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Zeskanuj kod QR w aplikacji mobilnej
                    </p>
                  </div>
                  
                  <div className="border-t pt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Kod QR (tekst):
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={qrCodeData.qrCodeValue}
                        readOnly
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm font-mono"
                      />
                      <button
                        onClick={handleCopyQrCode}
                        className="px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm"
                        title="Kopiuj kod"
                      >
                        Kopiuj
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center pt-4 border-t">
                    {qrCodeData?.employeeEmail && (
                      <button
                        onClick={handleSendQrCodeByEmail}
                        disabled={sendingEmail}
                        className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
                      >
                        <Send className="w-4 h-4" />
                        {sendingEmail ? 'Wysyłanie...' : 'Wyślij na email'}
                      </button>
                    )}
                    <div className="flex gap-2 ml-auto">
                      <button
                        onClick={() => {
                          setShowQrModal(false);
                          setQrCodeData(null);
                          setQrEmployeeId(null);
                        }}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                      >
                        Zamknij
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Błąd podczas ładowania kodu QR
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeesList;
